/* eslint-disable @tencent/i18next/no-literal-string */
import {
    ParagraphElement, LineSpacingType, DocumentElement, twipsToPx,
    IParagraphBox, ITextBox,
} from '@tencent/alloy-editor';
import { PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO } from '@src/slide/infrastructure/module/independent/unit';
import {
    AnalyzerResultType, IElementNode, ISlideNode,
    ITextElementBoxNode,
    ITextParagraphBoxNode,
} from '../slide-content-analyzer.interface';
import {
    FontSizeResult,
    FontSizeType,
} from '@src/slide/application/service/slide-rating-and-improve/text/result/font-size-result';
import {
    FontUseNumberResult,
} from '@src/slide/application/service/slide-rating-and-improve/text/result/font-use-number-result';
import {
    LineSpacingResult,
} from '@src/slide/application/service/slide-rating-and-improve/text/result/line-spacing-result';
import {
    TextOverlapAnalyzer,
} from '@src/slide/application/service/slide-rating-and-improve/text/text-overlap/text-overlap-analyzer';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { FONT_SCALE } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import i18n from '@i18next/i18n';

/**
 * 文本相关内容分析
 */
export class TextAnalyzer {
    /**
     * 标题字号检测结果
     */
    public titleFontSizeResult: FontSizeResult;
    /**
     * 副标题字号检测结果
     */
    public subTitleFontSizeResult: FontSizeResult;
    /**
     * 行间距检测结果
     */
    public lineSpacingResult: LineSpacingResult;
    /**
     * 字体数量检测结果
     */
    public fontUseNumberResult?: FontUseNumberResult;
    /**
     * 文字不重叠检测结果
     */
    public textOverlapResult: TextOverlapAnalyzer;
    private presentation: IPresentationModel;

    public constructor(
        forbidFontUseNumber: boolean,
        showUniformFontUI: (onHandled: () => void, onCancel: () => void) => void,
    ) {
        this.titleFontSizeResult = new FontSizeResult(i18n.t('标题字号检测'), i18n.t('建议不小于24号'), 24, FontSizeType.Title);
        this.subTitleFontSizeResult = new FontSizeResult(i18n.t('副标题字号检测'), i18n.t('建议不小于20号'), 20, FontSizeType.SubTitle);
        this.lineSpacingResult = new LineSpacingResult(i18n.t('行间距检测'), i18n.t('建议不小于1.2倍'), 1.2);
        if (!forbidFontUseNumber) {
            this.fontUseNumberResult = new FontUseNumberResult(i18n.t('使用字体数量检测'), i18n.t('建议不超过4个'), showUniformFontUI);
        }
        this.textOverlapResult = new TextOverlapAnalyzer(i18n.t('文字重叠检测'), i18n.t('建议文字之间没有重叠区域'));
    }

    public onEnumeratePresentationEnd() {
        this.titleFontSizeResult.onPresentationEnd();
        this.subTitleFontSizeResult.onPresentationEnd();
    }

    public onSlideEnd(slideNode: ISlideNode) {
        this.textOverlapResult.onSlideEnd(slideNode);
    }

    // eslint-disable-next-line complexity
    public analysisFontSize(textElementBoxNode: ITextElementBoxNode) {
        const { element, textBox } = textElementBoxNode;
        // @ts-ignore
        const isListMark = !!textBox.isListMark;
        if (isListMark) return;
        const style = textBox.textStyle;
        const fontSize = twipsToPx(style.fontSize) / FONT_SCALE;
        if (element.isPlaceholder) {
            if (element.isCtrTitlePl) {
                if (fontSize < this.titleFontSizeResult.minSize) {
                    this.titleFontSizeResult.resultType = AnalyzerResultType.ERROR;
                    this.titleFontSizeResult.resultInfo?.push(textElementBoxNode);
                }
            } else if (element.isTitleOrCtrTitlePl || element.isSubTitlePl) {
                if (fontSize < this.subTitleFontSizeResult.minSize) {
                    this.subTitleFontSizeResult.resultType = AnalyzerResultType.ERROR;
                    this.subTitleFontSizeResult.resultInfo?.push(textElementBoxNode);
                }
            }
        }
    }

    public analysisTextOverlapOfElement(elementNode: IElementNode) {
        this.textOverlapResult.checkNewElement(elementNode);
    }

    public analysisTextOverlapOfTextBox(textElementBoxNode: ITextElementBoxNode) {
        this.textOverlapResult.checkNewTextElementBoxNode(textElementBoxNode);
    }

    public analysisLineSpacing(textParagraphBoxNode: ITextParagraphBoxNode) {
        const paragraphIndex = textParagraphBoxNode.textBoxPath[textParagraphBoxNode.textBoxPath.length - 1];
        const { paraBox: paragraphBox, textDocumentElement } = textParagraphBoxNode;
        if (this.checkParagraphLineContent(paragraphBox, paragraphIndex, textParagraphBoxNode)) {
            // 超过一行的内容，检测行间距
            const paraData: ParagraphElement = textDocumentElement.children[paragraphIndex];
            if (typeof(paraData.lineSpacingRule) === 'undefined'
                || paraData.lineSpacingRule === LineSpacingType.Multiple) {
                const lineSpacing = Number(paraData.lineSpacing) / PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO;
                if (lineSpacing < this.lineSpacingResult.minSize) {
                    this.lineSpacingResult.resultType = AnalyzerResultType.ERROR;
                    this.lineSpacingResult.resultInfo?.push(textParagraphBoxNode);
                    // window.log.info('美化 lineSpacing:', slideIndex, lineSpacing);
                }
            }
        }
    }

    public analysisTextDocument(documentElement: DocumentElement) {
        documentElement.children.forEach((paragraphElement) => {
            // eslint-disable-next-line complexity
            paragraphElement.children.forEach((textElement) => {
                const textFont = textElement.fontFamily?.split(',')[0];
                if (textFont) {
                    let font;
                    if (textFont === '"微软雅黑"' || textFont === '"Microsoft YaHei"') {
                        font = '微软雅黑';
                    } else if (textFont === '"宋体"' || textFont === '"SimSun"' || textFont === '"Songti SC"') {
                        font = '宋体';
                    } else if (textFont === '"SimHei"' || textFont === '"Heiti SC"') {
                        font = 'SimHei';
                    } else if (textFont === '"KaiTi"' || textFont === '"KaiTi SC"') {
                        font = 'KaiTi';
                    } else {
                        font = textFont;
                    }

                    if (!textElement.lang || textElement.lang.toLowerCase() === 'ea') {
                        this.fontUseNumberResult?.eaFontSet.add(font);
                    }
                    if (!textElement.lang || textElement.lang.toLowerCase() === 'latin') {
                        this.fontUseNumberResult?.latinFontSet.add(font);
                    }
                    if (this.fontUseNumberResult && (this.fontUseNumberResult?.eaFontSet.size > 4
                        || this.fontUseNumberResult?.latinFontSet.size > 4)) {
                        this.fontUseNumberResult.resultType = AnalyzerResultType.ERROR;
                        this.fontUseNumberResult.resultInfo = Math.max(
                            this.fontUseNumberResult?.eaFontSet.size,
                            this.fontUseNumberResult?.latinFontSet.size,
                        );
                    }
                }
            });
        });
    }

    public resetWithPresentation(presentation: IPresentationModel) {
        this.reset();
        this.presentation = presentation;
        this.textOverlapResult.resetWithPresentation(presentation);
    }

    public reset() {
        this.titleFontSizeResult.reset();
        this.subTitleFontSizeResult.reset();
        this.lineSpacingResult.reset();
        this.fontUseNumberResult?.reset();
        this.textOverlapResult.reset();
    }

    /**
     * 检查段落里的行字数是否过短，过短就没必要检测行间距
     * @param paragraphBox
     * @param paragraphIndex
     * @param textParagraphBoxNode
     * @private
     */
    // eslint-disable-next-line complexity
    private checkParagraphLineContent(
        paragraphBox: IParagraphBox,
        paragraphIndex: number,
        textParagraphBoxNode: ITextParagraphBoxNode,
    ): boolean {
        if (this.lineSpacingResult.lineAnalyzerLastParaBox?.parentBox !== paragraphBox.parentBox) {
            this.lineSpacingResult.lastLineLength = 0;
        }
        this.lineSpacingResult.lineAnalyzerLastParaBox = paragraphBox;
        for (const lineBox of (paragraphBox as IParagraphBox).children) {
            let lineLength = 0;
            let lineFont = 0; // -1表示不止一个字体
            for (const textBox of lineBox.children) {
                lineLength += (textBox as ITextBox).text.length;
                // @ts-ignore
                const isListMark = !!textBox.isListMark;
                if (isListMark) {
                    // 项目符号开头的长度变成0
                    lineLength = 0;
                    continue;
                }
                const style = textBox.textStyle;
                const fontSize = twipsToPx(style.fontSize) / FONT_SCALE;
                if (lineFont === 0) {
                    lineFont = fontSize;
                } else if (lineFont !== fontSize) {
                    lineFont = -1;
                }
                if (fontSize > 30) {
                    lineLength = 0;
                    return false;
                }
            }
            if (this.lineSpacingResult.lastLineLength > 3 && lineLength > 3
                && this.lineSpacingResult.lastLineFont === lineFont && lineFont !== -1 && lineFont !== 0
            ) {
                // 相邻的两行都大于2个字符
                return true;
            }
            this.lineSpacingResult.lastLineLength = lineLength;
            this.lineSpacingResult.lastLineFont = lineFont;
        }
        return false;
    }
}
